import React, {
  useState,
  useRef,
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle
} from "react";
import {
  Form,
  Input,
  message,
  DatePicker,
  Row,
  Col,
  Modal,
  Button,
  Spin,
  Table,
} from "antd";
import {
  BankOutlined,
} from "@ant-design/icons";
import http from "@/utils/http";
import api from "@/utils/api";
import dayjs from "dayjs";
import commonFn from "@/utils/commonFn";

const EnterPaymentModal = forwardRef((props, ref) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [buildingData, setBuildingData] = useState([]);

  const columnStyle = {
    border: "1px solid #f0f0f0",
    padding: 20,
    borderRadius: 8,
    background: "#fafafa",
  };

  const totals = useCallback(() => {
    let totalAmount = 0;
    const uniquePropIds = new Set();
    buildingData.forEach((item) => {
      const num = parseFloat(String(item?.amount || "0").replace(/,/g, ""));
      if (!isNaN(num)) totalAmount += num;
      if (item?.propId) uniquePropIds.add(item.propId);
    });
    return {
      amount: totalAmount.toFixed(2),
      propCount: uniquePropIds.size,
      buildingCount: buildingData.length,
    };
  }, [buildingData]);

  const currentTotals = totals();

 
  const columns = useMemo(
    () => [
      {
        title: "Amount",
        dataIndex: "amount",
        width: 120,
        onCell: () => ({
          style: {
            padding: "8px",
          },
        }),
        render: (text) => (
          <div style={{ padding: "4px 0" }}>
            <Input
              prefix="$"
              value={text || ""}
              readOnly
            />
          </div>
        ),
      },
      {
        title: "Property",
        dataIndex: "propName",
        width: 320,
        onCell: () => ({
          style: {
            padding: "8px",
          },
        }),
        render: (text, record) => (
          <div style={{ padding: "4px 0" }}>
            <Input
              value={text || record.propId || "-"}
              readOnly
            />
          </div>
        ),
      },
      {
        title: "GL / Insure Type",
        dataIndex: "glAcctTitle",
        width: 140,
        onCell: () => ({
          style: {
            padding: "8px",
          },
        }),
        render: (text) => (
          <div style={{ padding: "4px 0" }}>
            <Input
              value={text || "-"}
              readOnly
            />
          </div>
        ),
      },
      {
        title: "Deductible",
        dataIndex: "liabilityDeductible",
        width: 150,
        onCell: () => ({
          style: {
            padding: "8px",
          },
        }),
        render: (text) => (
          <div style={{ padding: "4px 0" }}>
            <Input
              value={text || "-"}
              readOnly
            />
          </div>
        ),
      },
      {
        title: "Limit",
        dataIndex: "liabilityLimit",
        width: 150,
        onCell: () => ({
          style: {
            padding: "8px",
          },
        }),
        render: (text) => (
          <div style={{ padding: "4px 0" }}>
            <Input
              value={text || "-"}
              readOnly
            
            />
          </div>
        ),
      },
      {
        title: "Entity",
        dataIndex: "trustName",
        width: 140,
        onCell: () => ({
          style: {
            padding: "8px",
          },
        }),
        render: (text) => (
          <div style={{ padding: "4px 0" }}>
            <Input
              value={text || "-"}
              readOnly
            />
          </div>
        ),
      },
    ],
    [],
  );

  // 获取 Agent 
  const getAgentTypeList = async () => {
    try {
      const res = await http.get(api.getAgentType);
      const data = res?.data || res;
      const nameMap = {};
      (data || []).forEach((item) => {
        nameMap[String(item.vendorId)] = item.vendorName;
      });
      return nameMap;
    } catch (error) {
      console.error("Failed to get agent list:", error);
      return {};
    }
  };

  // 加载数据
  const loadData = async (id, pushBatch) => {
    if (!id) return;
    setLoading(true);
    try {
      // 获取 agent 映射
      const agentNameMap = await getAgentTypeList();
      const res = await http.get(api.getInsurancePaymentById, { params: { id,pushBatch   } });
      const data = res?.data;
      if (!data) {
        setBuildingData([]);
        return;
      }

      // 构建 building 列表
      const list = (data.buildingList || []).map((item, index) => ({
        _id: index + 1,
        amount: item.amount ? Number(item.amount).toFixed(2) : "0.00",
        propId: item.propId || null,
        propName: item.propName || "",
        glAcctTitle: item.glAcctTitle || "",
        liabilityDeductible: item.liabilityDeductible || "",
        liabilityLimit: item.liabilityLimit || "",
        trustName: item.trustName || item.owner || "",
        prop: item.prop || "",
        totalUnits: item.totalUnits || "",
        units: item.units || "",
      }));

      setBuildingData(list);

  
const agentDisplay = agentNameMap[String(data.agentVendorId)] || String(data.agentVendorId) || "-";
const payeeDisplay = agentNameMap[String(data.payeeVendorId)] || String(data.payeeVendorId) || "-";

    
      form.setFieldsValue({
        expirationDate: data.expirationDate ? dayjs(data.expirationDate).format("MM/DD/YYYY") : "-",
        effectiveDate: data.effectiveDate ? dayjs(data.effectiveDate).format("MM/DD/YYYY") : "-",
        policyNumber: data.policyNumber || "-",
        carrier: data.carrier || "-",
        agent: agentDisplay || "-",
        payee: payeeDisplay || "-",
      });
    } catch (err) {
      console.error("Failed to load details:", err);
      message.error("Failed to load details");
    } finally {
      setLoading(false);
    }
  };

  useImperativeHandle(ref, () => ({
    openModal: ({ record, pushBatch }) => {
      // console.log(record);
      setCurrentRecordId(record.id);
      setModalOpen(true);
      setBuildingData([]);
      form.resetFields();
       if (record?.id) {
      loadData(record.id,pushBatch);  
    }
    },
    closeModal: () => {
      setModalOpen(false);
      setCurrentRecordId(null);
      setBuildingData([]);
      form.resetFields();
    }
  }));

  const handleModalClose = () => {
    setModalOpen(false);
    setCurrentRecordId(null);
    setBuildingData([]);
    form.resetFields();
  };

  const panelStyle = {
    ...columnStyle,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  const existingRows = useMemo(
    () => buildingData,
    [buildingData],
  );
  return (
    <Modal
      title="Enter Payment"
      open={modalOpen}
      onCancel={handleModalClose}
      width="85vw"
      footer={  <div style={{ textAlign: "center", width: "100%" }}>
      <Button type="primary" onClick={handleModalClose}>
        Close
      </Button>
    </div>}
      destroyOnClose
      maskClosable={false}
      style={{ top: 20 }}
      bodyStyle={{
        padding: 20,
        maxHeight: "calc(100vh - 160px)",
      }}
    >
      <Spin spinning={loading} tip="Loading...">
          <div style={{ background: "#fff", borderRadius: 8 }}>
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 10 }}
        wrapperCol={{ span: 14 }}
      >
        <Row gutter={24} style={{ margin: 0 }}>
          <Col span={4}>
            <div style={columnStyle}>
              <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>
                <BankOutlined /> Insurance Detail
              </div>
              <Form.Item
                name="expirationDate"
                label="Expiration Date"
              >
                <Input readOnly style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item
                name="effectiveDate"
                label="Effective Date"
              >
                <Input readOnly style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="policyNumber" label="Policy Number">
                <Input  style={{ width: "100%" }} readOnly/>
              </Form.Item>
              <Form.Item name="carrier" label="Carrier">
                <Input  style={{ width: "100%" }} readOnly/>
              </Form.Item>
              <Form.Item name="agent" label="Agent">
                <Input  style={{ width: "100%" }}  readOnly />
              </Form.Item>
              <Form.Item name="payee" label="Payee">
                <Input  style={{ width: "100%" }} readOnly/>
              </Form.Item>
              <Form.Item label="Total Amount">
                <Input
                 readOnly
                  prefix="$"
                  value={commonFn.inputAmountFormat(
                    currentTotals.amount,
                    false,
                    true,
                  )}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item label="# of Properties">
                <Input 
                 readOnly 
                  value={currentTotals.propCount}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item label="# of Buildings">
                <Input 
                readOnly
                  value={currentTotals.buildingCount}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </div>
          </Col>

          <Col span={20}>
            <div style={panelStyle}>
              <div style={{ flex: 1, overflow: "auto", minHeight: 0 }}>
                <Table
                  rowClassName="table-row-gradient"
                  columns={columns}
                  dataSource={existingRows}
                  rowKey="_id"
                  className="no-border-table"
                  bordered={true}
                  showHeader={true}
                  sticky={{ offsetHeader: 0 }}
                  scroll={{ x: 'max-content', y: 'calc(100vh - 380px)' }}
                  pagination={{
                    defaultPageSize: 50,
                    pageSizeOptions: ["30", "50", "100", "200"],
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} items`,
                    position: ["bottomRight"],
                  }}
                  locale={{ emptyText: "No existing buildings." }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Form>
    </div>
      </Spin>
    </Modal>
  );
});
export default EnterPaymentModal;